import React, { useState } from "react";
import VideoEmbed from "./VideoEmbed";
import Lightbox from "./Lightbox";

/**
 * Renderer for the CMS ContentBlocks (image/video/dual/gallery/text) with per-block spacing.
 * - Images preserve their natural aspect (no crop) and are click-to-expand (lightbox).
 * - Videos preserve their aspect too.
 * - Every media element carries a `title` (tooltip) matching its alt text.
 * - New "text" block supports rich formatting + custom Google Font (from CMS).
 */

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
  return u;
}

const isVideoUrl = (u) => !!u && (/\.(mp4|webm|mov|m4v)/i.test(u) || /youtu\.?be|vimeo/i.test(u) || (u.startsWith("/api/media/") && /\.(mp4|webm|mov|m4v)/i.test(u)));

// Load a Google Font on-demand and cache it globally.
const loadedFonts = new Set();
function ensureGoogleFont(family) {
  if (!family || loadedFonts.has(family)) return;
  const id = `gfont-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) { loadedFonts.add(family); return; }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700&display=swap`;
  document.head.appendChild(link);
  loadedFonts.add(family);
}

export default function ContentBlocks({ blocks }) {
  const [lightbox, setLightbox] = useState(null); // { src, alt }
  if (!Array.isArray(blocks) || blocks.length === 0) return null;

  const openLightbox = (src, alt) => setLightbox({ src: resolveSrc(src), alt: alt || "" });
  const clickableImg = (src, alt, extraCls = "") => (
    <button
      type="button"
      onClick={() => openLightbox(src, alt)}
      className={`group block w-full text-left ${extraCls}`}
      data-testid="cb-image-btn"
      title={alt || ""}
    >
      <img
        src={resolveSrc(src)}
        alt={alt || ""}
        title={alt || ""}
        loading="lazy"
        className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.01]"
      />
    </button>
  );

  return (
    <>
      <div data-testid="content-blocks-render">
        {blocks.map((b, i) => {
          const gap = { marginBottom: `${Number.isFinite(Number(b.spacing_px)) ? Number(b.spacing_px) : 24}px` };

          if (b.type === "text") {
            if (b.font_family) ensureGoogleFont(b.font_family);
            const style = {
              fontFamily: b.font_family ? `'${b.font_family}', serif` : undefined,
              fontSize: b.font_size_px ? `${b.font_size_px}px` : undefined,
              fontWeight: b.font_weight || undefined,
              fontStyle: b.italic ? "italic" : undefined,
              textAlign: b.align || undefined,
              color: b.color || undefined,
              lineHeight: b.line_height || 1.5,
              letterSpacing: b.letter_spacing_em ? `${b.letter_spacing_em}em` : undefined,
              textTransform: b.uppercase ? "uppercase" : undefined,
              textDecoration: b.underline ? "underline" : undefined,
            };
            return (
              <div key={b.id || i} style={gap} data-testid={`cb-text-${i}`}>
                <div style={style} className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: b.html || (b.text || "").replace(/\n/g, "<br/>") }} />
              </div>
            );
          }

          if (b.type === "image") {
            return (
              <div key={b.id || i} style={gap} data-testid={`cb-image-${i}`}>
                {clickableImg(b.src, b.alt)}
              </div>
            );
          }

          if (b.type === "video") {
            return (
              <div key={b.id || i} style={gap} data-testid={`cb-video-${i}`}>
                <VideoEmbed src={b.src} poster={b.poster} title={b.alt || ""} aspect={b.aspect || "aspect-video"} preserveAspect={!b.aspect} />
              </div>
            );
          }

          if (b.type === "dual") {
            const L = b.left || {}, R = b.right || {};
            return (
              <div key={b.id || i} style={gap} className="flex flex-col md:flex-row gap-2 items-stretch" data-testid={`cb-dual-${i}`}>
                {[L, R].map((side, j) => {
                  if (!side.src) return <div key={j} className="flex-1 aspect-[4/3] bg-neutral-100" />;
                  if (side.kind === "video" || isVideoUrl(side.src)) {
                    return <div key={j} className="flex-1"><VideoEmbed src={side.src} poster={side.poster} title={side.alt || ""} aspect="aspect-video" /></div>;
                  }
                  return (
                    <button
                      key={j}
                      type="button"
                      onClick={() => openLightbox(side.src, side.alt || "")}
                      className="group flex-1 overflow-hidden md:h-[320px] block"
                      title={side.alt || ""}
                    >
                      <img
                        src={resolveSrc(side.src)}
                        alt={side.alt || ""}
                        title={side.alt || ""}
                        loading="lazy"
                        className="w-full h-full md:object-cover object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                      />
                    </button>
                  );
                })}
              </div>
            );
          }

          if (b.type === "gallery") {
            const items = Array.isArray(b.items) ? b.items : [];
            if (items.length === 0) return null;
            // Two layouts share the same full-bleed wrapper but differ on the
            // inner arrangement:
            //   • perRow (2..6) → hard grid, every row has EXACTLY N columns
            //     regardless of image aspect ratio. Cropped to a square-ish
            //     tile so heights align perfectly.
            //   • auto (default) → justified Behance-style, items grow by
            //     aspect ratio to fill each row.
            const perRow = Number(b.per_row) || 0;
            const fullBleed = {
              width: "100vw",
              position: "relative",
              left: "50%",
              right: "50%",
              marginLeft: "-50vw",
              marginRight: "-50vw",
              ...gap,
            };
            if (perRow >= 2 && perRow <= 6) {
              // Fixed grid — always N columns.
              const gridCls = {
                2: "grid-cols-1 sm:grid-cols-2",
                3: "grid-cols-2 md:grid-cols-3",
                4: "grid-cols-2 md:grid-cols-4",
                5: "grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
                6: "grid-cols-2 md:grid-cols-3 lg:grid-cols-6",
              }[perRow];
              return (
                <div key={b.id || i} style={fullBleed} className={`grid gap-2 w-screen ${gridCls}`} data-testid={`cb-gallery-${i}`}>
                  {items.map((it, k) => {
                    if (!it || !it.src) return null;
                    const isVideo = it.kind === "video" || isVideoUrl(it.src);
                    return (
                      <GridGalleryItem
                        key={k}
                        item={it}
                        isVideo={isVideo}
                        onOpenLightbox={openLightbox}
                      />
                    );
                  })}
                </div>
              );
            }
            // Justified (default).
            return (
              <div key={b.id || i} style={fullBleed} className="flex flex-wrap gap-2 w-screen" data-testid={`cb-gallery-${i}`}>
                {items.map((it, k) => {
                  if (!it || !it.src) return null;
                  const isVideo = it.kind === "video" || isVideoUrl(it.src);
                  return (
                    <JustifiedGalleryItem
                      key={k}
                      item={it}
                      isVideo={isVideo}
                      onOpenLightbox={openLightbox}
                    />
                  );
                })}
              </div>
            );
          }

          return null;
        })}
      </div>
      {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
    </>
  );
}

/**
 * Single item inside a justified gallery. Videos have a fixed 16:9 aspect; images
 * detect their natural aspect ratio on load. Every row STRETCHES to fill the full
 * container width by using `flex-grow` proportional to each item's aspect ratio,
 * so items within the same row all share the same visual height and there is no
 * trailing right-side gap.
 */
function JustifiedGalleryItem({ item, isVideo, onOpenLightbox }) {
  const [ratio, setRatio] = React.useState(isVideo ? 16 / 9 : 4 / 3);
  const targetH = 240;
  // Proportional flex-grow: item grows by weight = aspect ratio.
  // Flex-basis proportional to aspect too, so within a row all items share the
  // same computed height. `aspectRatio` CSS keeps intrinsic ratio while width
  // is determined by the flex algorithm. `maxHeight` caps the last (orphan)
  // row so a single item doesn't blow up huge to fill the entire row.
  const grow = Math.max(1, Math.round(ratio * 100));
  const basisPx = Math.round(ratio * targetH);
  const boxStyle = {
    flex: `${grow} 1 ${basisPx}px`,
    aspectRatio: String(ratio),
    maxHeight: `${Math.round(targetH * 1.6)}px`,
    minWidth: 120,
  };

  if (isVideo) {
    return (
      <div className="overflow-hidden bg-black rounded-sm" style={boxStyle} data-testid="cb-gallery-item">
        <VideoEmbed src={item.src} poster={item.poster} title={item.alt || ""} aspect="aspect-video" className="w-full h-full" />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpenLightbox(item.src, item.alt || "")}
      className="group overflow-hidden bg-neutral-100 rounded-sm relative"
      style={boxStyle}
      title={item.alt || ""}
      data-testid="cb-gallery-item"
    >
      <img
        src={resolveSrc(item.src)}
        alt={item.alt || ""}
        title={item.alt || ""}
        loading="lazy"
        onLoad={(e) => {
          const w = e.currentTarget.naturalWidth;
          const h = e.currentTarget.naturalHeight;
          if (w && h) setRatio(w / h);
        }}
        // object-cover fills the box entirely. Since the parent box's
        // aspectRatio matches the image's natural ratio (set on load), the
        // image is not cropped — it fits perfectly edge-to-edge.
        className="w-full h-full object-cover block transition-transform duration-500 group-hover:scale-[1.02]"
      />
    </button>
  );
}


/**
 * GridGalleryItem — used when the CMS sets `per_row` on a gallery block.
 * All items in the grid render as square-ish tiles (aspect-square) so the
 * layout stays perfectly aligned regardless of source image proportions. The
 * image itself uses `object-cover` (center-crop) to fill the tile cleanly.
 */
function GridGalleryItem({ item, isVideo, onOpenLightbox }) {
  if (isVideo) {
    return (
      <div className="overflow-hidden bg-black rounded-sm aspect-square" data-testid="cb-gallery-item">
        <VideoEmbed src={item.src} poster={item.poster} title={item.alt || ""} aspect="aspect-square" className="w-full h-full" />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpenLightbox(item.src, item.alt || "")}
      className="group overflow-hidden bg-neutral-100 rounded-sm relative aspect-square"
      title={item.alt || ""}
      data-testid="cb-gallery-item"
    >
      <img
        src={resolveSrc(item.src)}
        alt={item.alt || ""}
        title={item.alt || ""}
        loading="lazy"
        className="w-full h-full object-cover block transition-transform duration-500 group-hover:scale-[1.02]"
      />
    </button>
  );
}

